import { call } from '@advanza/api'
import { Col } from '@advanza/grid'
import { InputField } from '@advanza/input'
import { Button, Divider, HoverText } from '@advanza/ui'
import { fetchProvider } from 'actions/providers'
import AutoRow from 'components/misc/AutoRow'
import { useToast } from 'components/misc/Toaster'
import { useAppDispatch } from 'hooks/hooks'
import React, { useState } from 'react'

const REQUEST_TYPE_POST = 'POST'
const REQUEST_TYPE_DEL = 'DELETE'
const REQUEST_TYPE_FORCE_NEW = 'FORCE_NEW'

const STATUS_PENDING = 'PENDING'
const STATUS_DELETED = 'DELETED'
const STATUS_REQUESTED_PREFIX = 'REQUESTED_'

const REQUEST_TYPES_WITH_AREACODE = [REQUEST_TYPE_POST, REQUEST_TYPE_FORCE_NEW]

const usePhoneRouting = (provider) => {
    const [isFetching, setIsFetching] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const [isWorking, setIsWorking] = useState(false)
    const [areacode, setAreacode] = useState('')
    const dispatch = useAppDispatch()
    const { addToast } = useToast()

    const makeRouteNr = () => requestPhoneRouteNr(REQUEST_TYPE_POST)

    const deleteRouteNr = () => requestPhoneRouteNr(REQUEST_TYPE_DEL)

    const forceNewRouteNr = () => requestPhoneRouteNr(REQUEST_TYPE_FORCE_NEW)

    const requestPhoneRouteNr = (requestType) => {
        const providerId = provider.service_provider_id
        const endPoints = {
            [REQUEST_TYPE_POST]: 'get-routing-nr',
            [REQUEST_TYPE_FORCE_NEW]: 'force-new-routing-nr',
            [REQUEST_TYPE_DEL]: 'del-routing-nr',
        }
        const supportsAreacode = REQUEST_TYPES_WITH_AREACODE.includes(requestType)
        const endPoint = endPoints[requestType]

        let url = 'office/phone-routing/' + endPoint + '/' + providerId

        if (supportsAreacode && areacode) {
            url += '/' + areacode
        }

        setIsFetching(true)
        call(url).then(
            () => {
                setIsFetching(false)
                setErrorMsg('')
                setIsWorking({
                    msg: 'Qooqie API received call. It may take a few minutes to process. Refresh this page. It may take up to a day for BE.',
                })
                dispatch(fetchProvider(providerId, true))
            },
            (reason) => {
                setIsFetching(false)
                const msg = reason?.error ?? JSON.stringify(reason, null, 2)
                setErrorMsg(msg)
                addToast({ msg, fromTop: true })
                dispatch(fetchProvider(providerId, true))
            },
        )
    }

    const onAreacodeChange = (e) => setAreacode(e.target.value)

    return {
        isFetching,
        errorMsg,
        isWorking,
        forceNewRouteNr,
        makeRouteNr,
        deleteRouteNr,
        areacode,
        onAreacodeChange,
    }
}

const PhoneRouting = ({ provider }) => {
    const {
        isFetching,
        errorMsg,
        forceNewRouteNr,
        makeRouteNr,
        deleteRouteNr,
        isWorking,
        areacode,
        onAreacodeChange,
    } = usePhoneRouting(provider)
    const routingNr = provider.phone_routing_nr
    const isDeleted = routingNr === STATUS_DELETED
    const hasNoNumber =
        !routingNr || routingNr === STATUS_PENDING || routingNr.startsWith(STATUS_REQUESTED_PREFIX)
    const hasNumber = !hasNoNumber && !isDeleted
    const buttonsDisabled = isFetching || errorMsg || isWorking
    const areacodeField = (
        <HoverText
            trigger={
                <InputField
                    value={areacode}
                    inputPlaceholder="areacode"
                    onChange={onAreacodeChange}
                />
            }>
            <span>Here you can use a separate areacode for requesting the Qooqie number</span>
        </HoverText>
    )
    return (
        <AutoRow>
            {hasNoNumber && (
                <>
                    {areacodeField}
                    <Button disabled={buttonsDisabled} onClick={makeRouteNr}>
                        {isFetching ? 'busy...' : 'get nr'}
                    </Button>
                    <Divider />
                </>
            )}
            {hasNumber && (
                <>
                    <Button disabled={buttonsDisabled} onClick={deleteRouteNr}>
                        {isFetching ? 'busy...' : 'del nr'}
                    </Button>
                    <Divider />
                    {areacodeField}
                    <Button
                        title={
                            provider.country_code === 'NL'
                                ? undefined
                                : 'Cannot change Qooqie number for non-NL pros'
                        }
                        disabled={buttonsDisabled || provider.country_code !== 'NL'}
                        onClick={forceNewRouteNr}>
                        {isFetching ? 'busy...' : 'new nr'}
                    </Button>
                    <Divider />
                </>
            )}
            {isDeleted && (
                <Button disabled={buttonsDisabled} onClick={makeRouteNr}>
                    {isFetching ? 'busy...' : 'restore nr'}
                </Button>
            )}
            {isWorking && <Col x>{<p className="">{isWorking.msg}</p>}</Col>}
            {errorMsg && (
                <div>
                    <p className="error">{errorMsg}</p>
                </div>
            )}
        </AutoRow>
    )
}

export default PhoneRouting
